// src/components/Admin/AdminEditInvoice.jsx

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TextField,
  Button,
  Autocomplete,
  Paper,
  Typography,
  Divider,
  IconButton,
  Grid,
  Box,
  MenuItem,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { server } from '../server';
import { useParams, useNavigate } from 'react-router-dom';

const AdminEditInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State variables
  const [invoiceData, setInvoiceData] = useState(null);
  const [buyerOptions, setBuyerOptions] = useState([]);
  const [buyerDetails, setBuyerDetails] = useState(null);
  const [items, setItems] = useState([
    {
      name: '',
      description: '',
      qty: 1,
      pricePerQty: 'Nos',
      pricePerQtyWithoutGST: '',
      gstPercentage: '',
      hsnCode: '',
      totalPrice: 0,
    },
  ]);
  const [shippingCharges, setShippingCharges] = useState(0);
  const [totalValueWithoutGst, setTotalValueWithoutGst] = useState(0);
  const [totalGstValue, setTotalGstValue] = useState(0);
  const [totalInvoiceValue, setTotalInvoiceValue] = useState(0);
  const [customerOrderNumber, setCustomerOrderNumber] = useState(''); // Renamed from purchaseOrderNumber for consistency
  const [paymentMethod, setPaymentMethod] = useState('Cash On Delivery');
  const [invoiceDate, setInvoiceDate] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    return today;
  });
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [loadingInvoiceData, setLoadingInvoiceData] = useState(false);

  // State for shipping address
  const [shippingAddress, setShippingAddress] = useState({
    address: '',
    zipCode: '',
  });

  // Fetch invoice data on mount
  useEffect(() => {
    fetchInvoiceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Populate state when invoiceData is fetched
  useEffect(() => {
    if (invoiceData) {
      setBuyerDetails(invoiceData.buyerDetails);
      setItems(
        invoiceData.items.map((item) => ({
          ...item,
          pricePerQty: item.pricePerQty || 'Nos',
          pricePerQtyWithoutGST: item.discountPriceWithCommission || '',
        }))
      );
      setShippingCharges(invoiceData.shippingCharges);
      setTotalValueWithoutGst(invoiceData.totalValueWithoutGst.toFixed(2));
      setTotalGstValue(invoiceData.totalGstValue.toFixed(2));
      setTotalInvoiceValue(invoiceData.totalInvoiceValue.toFixed(2));
      setInvoiceNumber(invoiceData.invoiceNumber);
      setCustomerOrderNumber(invoiceData.customerOrderNumber || '');
      setPaymentMethod(invoiceData.paymentMethod);
      setShippingAddress(invoiceData.shippingAddress);
      setInvoiceDate(invoiceData.invoiceDate || new Date().toISOString().split('T')[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData]);

  // Recalculate totals when items or shipping charges change
  useEffect(() => {
    calculateTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, shippingCharges]);

  // Fetch the specific invoice data
  const fetchInvoiceData = async () => {
    setLoadingInvoiceData(true);
    try {
      if (!id) {
        throw new Error('Invoice ID is missing.');
      }
      const response = await axios.get(`${server}/invoice/get-invoice/${id}`, {
        withCredentials: true,
      });
      if (response.data.success) {
        setInvoiceData(response.data.invoice);
      } else {
        alert('Invoice not found.');
      }
    } catch (error) {
      console.error('Error fetching invoice data:', error);
      alert(error.message || 'Failed to fetch invoice data.');
    } finally {
      setLoadingInvoiceData(false);
    }
  };

  // Handle buyer search for autocomplete
  const handleBuyerSearch = async (event, value) => {
    const query = value;
    if (query && query.length >= 1) {
      try {
        const response = await axios.get(`${server}/user/search?query=${query}`, {
          withCredentials: true,
        });
        if (response.data.success) {
          setBuyerOptions(response.data.users);
        }
      } catch (error) {
        console.error('Error fetching buyer options:', error);
      }
    } else {
      setBuyerOptions([]);
    }
  };

  // Handle buyer selection from autocomplete
  const handleBuyerSelect = (event, value) => {
    setBuyerDetails(value);
    if (value) {
      setShippingAddress({
        address: value.billingAddress || '',
        zipCode: value.zipCode || '',
      });
    } else {
      setShippingAddress({
        address: '',
        zipCode: '',
      });
    }
  };

  // Handle changes in item fields
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;

    // Update totalPrice for the item
    if (['qty', 'pricePerQtyWithoutGST', 'gstPercentage'].includes(field)) {
      const qty = parseFloat(updatedItems[index].qty) || 0;
      const pricePerQtyWithoutGST = parseFloat(updatedItems[index].pricePerQtyWithoutGST) || 0;
      const gstPercentage = parseFloat(updatedItems[index].gstPercentage) || 0;

      const priceWithoutGst = qty * pricePerQtyWithoutGST;
      const gstValue = (priceWithoutGst * gstPercentage) / 100;
      const total = priceWithoutGst + gstValue;

      updatedItems[index].totalPrice = total;
    }

    setItems(updatedItems);
  };

  // Add a new item row
  const handleAddItem = () => {
    setItems([
      ...items,
      {
        name: '',
        description: '',
        qty: 1,
        pricePerQty: 'Nos',
        pricePerQtyWithoutGST: '',
        gstPercentage: '',
        hsnCode: '',
        totalPrice: 0,
      },
    ]);
  };

  // Remove an item row
  const handleRemoveItem = (index) => {
    const updatedItems = items.filter((_, idx) => idx !== index);
    setItems(updatedItems);
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalWithoutGst = 0;
    let totalGst = 0;

    items.forEach((item) => {
      const qty = parseFloat(item.qty) || 0;
      const pricePerQtyWithoutGST = parseFloat(item.pricePerQtyWithoutGST) || 0;
      const gstPercentage = parseFloat(item.gstPercentage) || 0;

      const priceWithoutGst = qty * pricePerQtyWithoutGST;
      const gstValue = (priceWithoutGst * gstPercentage) / 100;

      totalWithoutGst += priceWithoutGst;
      totalGst += gstValue;
    });

    const totalValue = totalWithoutGst + totalGst + parseFloat(shippingCharges || 0);

    setTotalValueWithoutGst(totalWithoutGst.toFixed(2));
    setTotalGstValue(totalGst.toFixed(2));
    setTotalInvoiceValue(totalValue.toFixed(2));
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (!buyerDetails) {
      alert('Please select a buyer.');
      return;
    }

    if (!customerOrderNumber) {
      alert('Please enter a Customer Order Number.');
      return;
    }

    if (!shippingAddress.address || !shippingAddress.zipCode) {
      alert('Please enter the shipping address.');
      return;
    }

    if (!invoiceDate) {
      alert('Please select an invoice date.');
      return;
    }

    // Prepare buyerDetails data
    const buyerDetailsData = {
      userId: buyerDetails._id || buyerDetails.userId,
      name: buyerDetails.name,
      billingAddress: buyerDetails.billingAddress,
      zipCode: buyerDetails.zipCode,
      gstNumber: buyerDetails.gstNumber,
      phoneNumber: buyerDetails.phoneNumber,
      email: buyerDetails.email,
      address: shippingAddress.address,
    };

    // Prepare items data
    const itemsData = items.map((item, index) => {
      const qty = parseFloat(item.qty) || 0;
      const pricePerQtyWithoutGST = parseFloat(item.pricePerQtyWithoutGST) || 0;
      const gstPercentage = parseFloat(item.gstPercentage) || 0;

      const priceWithoutGst = qty * pricePerQtyWithoutGST;
      const gstValue = (priceWithoutGst * gstPercentage) / 100;
      const totalPrice = priceWithoutGst + gstValue;

      return {
        name: item.name,
        description: item.description,
        qty: qty,
        gstPercentage: gstPercentage,
        pricePerQty: item.pricePerQty, // Unit
        totalPrice: priceWithoutGst.toFixed(2),
        hsnCode: item.hsnCode,
        sno: index + 1,
        discountPriceWithCommission: pricePerQtyWithoutGST.toFixed(2), // Price without GST
        totalPriceWithCommission: totalPrice.toFixed(2),
      };
    });

    // Calculate totalValueWithCommission
    const totalValueWithCommission = parseFloat(totalValueWithoutGst) + parseFloat(totalGstValue);

    // Prepare updated invoice data
    const updatedInvoiceData = {
      orderId: invoiceData.orderId, // Ensure orderId remains unchanged
      buyerDetails: buyerDetailsData,
      shippingAddress, // Include shipping address
      items: itemsData,
      shippingCharges: parseFloat(shippingCharges),
      totalValueWithoutGst: parseFloat(totalValueWithoutGst),
      totalGstValue: parseFloat(totalGstValue),
      totalInvoiceValue: parseFloat(totalInvoiceValue),
      totalValueWithCommission: totalValueWithCommission,
      purchaseOrderNumber: customerOrderNumber, // Customer Order Number
      paymentMethod,
      invoiceDate, // Include invoice date
    };

    try {
      const response = await axios.put(`${server}/invoice/edit-invoice/${id}`, updatedInvoiceData, {
        withCredentials: true,
      });
      if (response.data.success) {
        alert('Invoice updated successfully!');
        navigate('/admin-sales'); // Redirect to sales page or appropriate route
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
      alert('Failed to update invoice.');
    }
  };

  // Handle form reset
  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset the form?')) {
      if (invoiceData) {
        setBuyerDetails(invoiceData.buyerDetails);
        setItems(
          invoiceData.items.map((item) => ({
            ...item,
            pricePerQty: item.pricePerQty || 'Nos',
            pricePerQtyWithoutGST: item.discountPriceWithCommission || '',
          }))
        );
        setShippingCharges(invoiceData.shippingCharges);
        setCustomerOrderNumber(invoiceData.customerOrderNumber || '');
        setPaymentMethod(invoiceData.paymentMethod);
        setShippingAddress(invoiceData.shippingAddress);
        setInvoiceDate(invoiceData.invoiceDate || new Date().toISOString().split('T')[0]);
        calculateTotals();
      } else {
        // If invoiceData is not loaded, reset to initial state
        setBuyerDetails(null);
        setShippingAddress({ address: '', zipCode: '' });
        setItems([
          {
            name: '',
            description: '',
            qty: 1,
            pricePerQty: 'Nos',
            pricePerQtyWithoutGST: '',
            gstPercentage: '',
            hsnCode: '',
            totalPrice: 0,
          },
        ]);
        setShippingCharges(0);
        setCustomerOrderNumber('');
        setPaymentMethod('Cash On Delivery');
        setTotalValueWithoutGst(0);
        setTotalGstValue(0);
        setTotalInvoiceValue(0);
        setInvoiceDate(new Date().toISOString().split('T')[0]);
      }
    }
  };

  return (
    <Paper sx={{ padding: 4, margin: '20px auto', maxWidth: '1200px' }}>
      <Typography variant="h4" gutterBottom>
        Edit GST Invoice
      </Typography>
      <Divider sx={{ marginY: 2 }} />

      {loadingInvoiceData ? (
        <Typography>Loading invoice data...</Typography>
      ) : invoiceData ? (
        <>
          {/* Buyer, Invoice Date, and Invoice Number */}
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={buyerOptions}
                getOptionLabel={(option) => `${option.name} - ${option.email}`}
                onInputChange={handleBuyerSearch}
                onChange={handleBuyerSelect}
                renderInput={(params) => (
                  <TextField {...params} label="Search Buyer" variant="outlined" />
                )}
                fullWidth
                value={buyerDetails}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Invoice Date"
                type="date"
                variant="outlined"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Invoice Number"
                variant="outlined"
                fullWidth
                value={invoiceNumber}
                InputProps={{
                  readOnly: true,
                }}
                disabled
              />
            </Grid>
          </Grid>

          {buyerDetails && (
            <Box sx={{ marginTop: 3 }}>
              <Typography variant="h6">Buyer Details</Typography>
              <Grid container spacing={2} sx={{ marginTop: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Name:</strong> {buyerDetails.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Email:</strong> {buyerDetails.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Phone Number:</strong> {buyerDetails.phoneNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>GST Number:</strong> {buyerDetails.gstNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography>
                    <strong>Billing Address:</strong> {buyerDetails.billingAddress}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Zip Code:</strong> {buyerDetails.zipCode}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Shipping Address */}
          <Box sx={{ marginTop: 4 }}>
            <Typography variant="h6">Shipping Address</Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ marginY: 2 }}
              onClick={() => {
                if (buyerDetails) {
                  setShippingAddress({
                    address: buyerDetails.billingAddress,
                    zipCode: buyerDetails.zipCode,
                  });
                }
              }}
            >
              Use Billing Address
            </Button>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Shipping Address"
                  variant="outlined"
                  fullWidth
                  value={shippingAddress.address}
                  onChange={(e) =>
                    setShippingAddress({ ...shippingAddress, address: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Zip Code"
                  variant="outlined"
                  fullWidth
                  value={shippingAddress.zipCode}
                  onChange={(e) =>
                    setShippingAddress({ ...shippingAddress, zipCode: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </Box>

          {/* Invoice Details */}
          <Box sx={{ marginTop: 4 }}>
            <Typography variant="h6">Invoice Details</Typography>
            <Grid container spacing={4} sx={{ marginTop: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Customer Order Number"
                  variant="outlined"
                  fullWidth
                  value={customerOrderNumber}
                  onChange={(e) => setCustomerOrderNumber(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Method"
                  variant="outlined"
                  fullWidth
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <MenuItem value="Razorpay">Razorpay</MenuItem>
                  <MenuItem value="Cash On Delivery">Cash On Delivery</MenuItem>
                  <MenuItem value="Pay on Credit">Pay on Credit</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>

          {/* Items */}
          <Box sx={{ marginTop: 4 }}>
            <Typography variant="h6">Items</Typography>
            {items.map((item, index) => (
              <Paper
                key={index}
                sx={{ padding: 2, marginTop: 2, backgroundColor: '#f9f9f9' }}
                elevation={3}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Item Name"
                      variant="outlined"
                      fullWidth
                      value={item.name}
                      onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Description"
                      variant="outlined"
                      fullWidth
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Quantity"
                      variant="outlined"
                      type="number"
                      fullWidth
                      value={item.qty}
                      onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Price / Qty (Without GST)"
                      variant="outlined"
                      type="number"
                      fullWidth
                      value={item.pricePerQtyWithoutGST}
                      onChange={(e) =>
                        handleItemChange(index, 'pricePerQtyWithoutGST', e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      select
                      label="Price per Unit"
                      variant="outlined"
                      fullWidth
                      value={item.pricePerQty}
                      onChange={(e) => handleItemChange(index, 'pricePerQty', e.target.value)}
                    >
                      {[
                        'Nos',
                        'Kilogram',
                        'Gram',
                        'Meter',
                        'Millimeter',
                        'Centimeter',
                        'Foot',
                        'Square foot',
                        'Litre',
                        'Millilitre',
                        'Cubic meter',
                        'Dozen',
                      ].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="GST (%)"
                      variant="outlined"
                      type="number"
                      fullWidth
                      value={item.gstPercentage}
                      onChange={(e) => handleItemChange(index, 'gstPercentage', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="HSN Code"
                      variant="outlined"
                      fullWidth
                      value={item.hsnCode}
                      onChange={(e) => handleItemChange(index, 'hsnCode', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Total Price"
                      variant="outlined"
                      type="number"
                      fullWidth
                      value={parseFloat(item.totalPrice).toFixed(2)}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <IconButton color="error" onClick={() => handleRemoveItem(index)}>
                      <Remove />
                    </IconButton>
                    {index === items.length - 1 && (
                      <IconButton color="primary" onClick={handleAddItem}>
                        <Add />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>

          {/* Shipping and Totals */}
          <Box sx={{ marginTop: 4 }}>
            <Typography variant="h6">Additional Charges & Totals</Typography>
            <Grid container spacing={4} sx={{ marginTop: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Additional Shipping Charges"
                  variant="outlined"
                  type="number"
                  fullWidth
                  value={shippingCharges}
                  onChange={(e) => setShippingCharges(e.target.value)}
                />
              </Grid>
            </Grid>
            <Divider sx={{ marginY: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography>
                  <strong>Total Value (Without GST):</strong> Rs {totalValueWithoutGst}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography>
                  <strong>Total GST Value:</strong> Rs {totalGstValue}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography>
                  <strong>Total Invoice Value:</strong> Rs {totalInvoiceValue}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ marginTop: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" color="secondary" onClick={handleReset}>
              Reset
            </Button>
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              Save Changes
            </Button>
          </Box>
        </>
      ) : (
        <Typography>Invoice not found.</Typography>
      )}
    </Paper>
  );
};

export default AdminEditInvoice;
